import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * Searchable combobox for entity pickers and filter dropdowns.
 */
export default function SearchableSelect({
  items = [],
  value = '',
  onChange,
  getValue = item => item?.value ?? item?.id ?? '',
  getLabel = item => item?.label ?? item?.name ?? String(getValue(item) ?? ''),
  getSearchText,
  renderOption,
  renderSelected,
  placeholder = 'Search…',
  hint,
  showHint = false,
  allowEmpty = false,
  emptyLabel = '',
  compact = false,
  className = '',
  disabled = false,
  loading = false,
  noResultsMessage = 'No matches found',
  clearAriaLabel = 'Clear selection'
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const resolveSearchText = item => {
    if (getSearchText) return getSearchText(item);
    const label = getLabel(item);
    const id = getValue(item);
    return `${label} ${id}`.trim().toLowerCase();
  };

  const selected = useMemo(
    () => items.find(item => getValue(item) === value) ?? null,
    [items, value, getValue]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...items].sort((a, b) =>
      getLabel(a).localeCompare(getLabel(b), undefined, { sensitivity: 'base' })
    );
    if (!q) return list;
    return list.filter(item => resolveSearchText(item).includes(q));
  }, [items, query, getLabel, getSearchText]);

  useEffect(() => {
    if (!value) {
      if (!open) setQuery('');
      return;
    }
    if (selected && !open) {
      setQuery(renderSelected ? String(renderSelected(selected) ?? '') : getLabel(selected));
    }
  }, [value, selected, open, getLabel, renderSelected]);

  useEffect(() => {
    const onDocClick = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const pick = item => {
    onChange(getValue(item));
    setQuery(renderSelected ? String(renderSelected(item) ?? '') : getLabel(item));
    setOpen(false);
  };

  const clearSelection = () => {
    onChange('');
    setQuery('');
    setOpen(false);
  };

  const handleInputChange = e => {
    const next = e.target.value;
    setQuery(next);
    setOpen(true);
    if (!next.trim()) {
      if (allowEmpty) onChange('');
      return;
    }
    if (selected && next.trim() !== getLabel(selected)) {
      onChange('');
    }
  };

  const inputClass = compact
    ? "h-9 w-full min-w-0 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 pr-7 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
    : "h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 pr-8 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  const displayPlaceholder =
    allowEmpty && !value && emptyLabel ? emptyLabel : placeholder;

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <input
        type="text"
        className={inputClass}
        placeholder={displayPlaceholder}
        value={query}
        onChange={handleInputChange}
        onFocus={() => !disabled && setOpen(true)}
        disabled={disabled || loading}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={open}
      />
      {(value || query) && !disabled && (
        <button
          type="button"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-muted-fg))] text-sm leading-none p-1"
          onClick={clearSelection}
          aria-label={clearAriaLabel}
        >
          ✕
        </button>
      )}
      {open && !disabled && (
        <ul
          className="absolute z-30 left-0 right-0 mt-1 max-h-52 overflow-y-auto rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)] text-sm"
          role="listbox"
        >
          {loading ? (
            <li className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">Loading…</li>
          ) : filtered.length === 0 ? (
            <li className="px-3 py-2 text-[hsl(var(--ds-muted-fg))]">{noResultsMessage}</li>
          ) : (
            filtered.map(item => {
              const id = getValue(item);
              return (
                <li key={id || getLabel(item)} role="option" aria-selected={id === value}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2 hover:bg-[hsl(var(--ds-primary)/0.08)] ${
                      id === value ? 'bg-[hsl(var(--ds-primary)/0.06)] font-medium text-[hsl(var(--ds-primary))]' : 'text-[hsl(var(--ds-fg))]'
                    }`}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => pick(item)}
                  >
                    {renderOption ? renderOption(item, { selected: id === value }) : getLabel(item)}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
      {showHint && hint && <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-1">{hint}</p>}
    </div>
  );
}
